export function editorRange(editor: HTMLElement): Range | null {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return null;
  const range = selection.getRangeAt(0);
  return editor.contains(range.startContainer) && editor.contains(range.endContainer) ? range : null;
}

export function selectedElements(editor: HTMLElement, range: Range): HTMLElement[] {
  const parent = (node: Node) => node instanceof HTMLElement ? node : node.parentElement!;
  if (range.collapsed) return [parent(range.startContainer)];
  const nodes: HTMLElement[] = [];
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (!node.textContent?.length || !range.intersectsNode(node)) continue;
    // intersectsNode also includes boundary text nodes with zero selected chars.
    if (node === range.startContainer && range.startOffset === node.textContent.length) continue;
    if (node === range.endContainer && range.endOffset === 0) continue;
    nodes.push(parent(node));
  }
  return nodes.length ? nodes : [parent(range.startContainer)];
}

export function inEditorAncestor(element: HTMLElement, selector: string, editor: HTMLElement): boolean {
  const match = element.closest(selector);
  return !!match && match !== editor && editor.contains(match);
}

export function readFormatting(editor: HTMLElement): Record<string, boolean> {
  const range = editorRange(editor);
  if (!range) return {};
  const elements = selectedElements(editor, range);
  const all = (selector: string) => elements.every((element) => inEditorAncestor(element, selector, editor));
  return {
    bold: document.queryCommandState("bold"),
    italic: document.queryCommandState("italic"),
    underline: document.queryCommandState("underline"),
    h1: all("h1"), h2: all("h2"), blockquote: all("blockquote"), pre: all("pre"),
    insertUnorderedList: document.queryCommandState("insertUnorderedList"),
    insertOrderedList: document.queryCommandState("insertOrderedList"),
    link: all("a[href]"),
  };
}

export function toggleBlock(editor: HTMLElement, tag: string) {
  const active = readFormatting(editor)[tag];
  if (active && tag === "blockquote") {
    document.execCommand("outdent");
  } else {
    document.execCommand("formatBlock", false, active ? "p" : tag);
  }
}

export function unlinkSelection(editor: HTMLElement) {
  const range = editorRange(editor);
  if (range?.collapsed) {
    const element = range.startContainer instanceof HTMLElement ? range.startContainer : range.startContainer.parentElement;
    const anchor = element?.closest("a");
    // Chromium's unlink is a no-op at a caret; select the current link first.
    if (anchor && editor.contains(anchor)) range.selectNodeContents(anchor);
  }
  document.execCommand("unlink");
}

export function clearFormatting(editor: HTMLElement) {
  // removeFormat handles inline marks but not links, headings, lists or quotes.
  // Native editing commands preserve selection, unselected text and undo history.
  document.execCommand("removeFormat");
  unlinkSelection(editor);
  document.execCommand("formatBlock", false, "p");
  for (let depth = 0; depth < 20; depth++) {
    const range = editorRange(editor);
    if (!range || !selectedElements(editor, range).some((element) => inEditorAncestor(element, "li,blockquote", editor))) break;
    const before = editor.innerHTML;
    document.execCommand("outdent");
    if (editor.innerHTML === before) break;
  }
  document.execCommand("removeFormat");
}
